import Search from "@/assets/icons/Search";
import { usePermissions } from "@/auth/PermissionsContext";
import BadgeShared from "@/components/shared/Badge/Badge";
import CardCurrency from "@/components/shared/CardCurrency/CardCurrency";
import CustomSheet from "@/components/shared/Sheet/Sheet";
import CTATable from "@/components/shared/TableListCustom/CTATable";
import TableListCustom from "@/components/shared/TableListCustom/TableListCustom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import DateRangePicker from "@/pages/Dashboard/Partials/DateRangePicker";
import { ExportButton } from "@/components/shared/ExportButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TableCell } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import LayoutInside from "@/layout/LayoutInside";
import { cn } from "@/lib/utils";
import useModalStore from "@/store/useModalStore";
import { getIconMethodPayment } from "@/utils/getIconMethodPayment";
import {
  canCancelOrReturn,
  canMarkAsDeliveryFailed,
  FILTER_TABS,
  getAllowedTransitionsForDropdown,
  getAllowedTransitionsForMultiple,
  STATUS_ICONS,
} from "@/utils/orders";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  ChevronDown,
  ListFilter,
  Package,
  Search as SearchIcon,
  Truck,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CANCEL_ORDER_MODAL_NAME, CancelOrderModal } from "./components/CancelOrderModal";
import { DELIVERY_FAILED_MODAL_NAME, DeliveryFailedModal } from "./components/DeliveryFailedModal";
import FilterOrders, { FILTER_ORDERS_MODAL } from "./components/FilterOrders";
import { TRACKING_MODAL_NAME, TrackingModal } from "./components/TrackingModal";
import { Order, OrderStatus, useOrders } from "./hooks/useOrders";

export default function Orders() {
  const { t } = useTranslation(["common", "orders"]);
  const navigate = useNavigate();

  const { hasPermission } = usePermissions();
  const canChangeStatus = hasPermission('orders.change_processing_status');
  const canAddEditTracking = hasPermission('orders.add_edit_tracking');
  const canMarkDeliveryFailure = hasPermission('orders.mark_delivery_failure');
  const canCancelOrder = hasPermission('orders.cancel_order');
  const canReturnOrder = hasPermission('orders.return_order');
  const canViewOrderDetails = hasPermission('order_details.view_order_details');

  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [selectedOrderForTracking, setSelectedOrderForTracking] = useState<Order | null>(null);
  const [selectedOrderForDeliveryFailed, setSelectedOrderForDeliveryFailed] = useState<Order | null>(null);
  const [selectedOrderForCancel, setSelectedOrderForCancel] = useState<Order | null>(null);
  const { openModal, closeModal, modals } = useModalStore();

  const {
    orders,
    tableData,
    summaryCounts,
    activeTab,
    setActiveTab,
    searchQuery,
    handleSearchChange,
    currentPage,
    setCurrentPage,
    total,
    isLoading,
    isLoadingSummary,
    updateOrderStatus,
    filterParams,
    setFilterParams,
    filterVisualParams,
    hasActiveFilters,
    removeFilterItem,
    removeFilter,
    clearAllFilters,
    filterBadgeLabel,
    keyLabel,
    dateRange,
    handleDateRangeChange,
    exportApiParams,
  } = useOrders();

  const MAX_VISIBLE_FILTERS = 15;

  const allFilterBadges = useMemo(() => {
    const badges: { key: string; group: string; label: string; onClick: () => void }[] = [];
    Object.entries(filterVisualParams).forEach(([key, values]) => {
      if (typeof values === "string" && values === "") return;

      if (
        Array.isArray(values) &&
        values.length > 0 &&
        typeof values[0] === "string"
      ) {
        (values as string[]).forEach((value, index) => {
          badges.push({
            key: `${key}-${index}`,
            group: `${keyLabel[key]}:`,
            label: `${filterBadgeLabel[value] || value}`,
            onClick: () => removeFilterItem(key, index),
          });
        });
      } else if (Array.isArray(values) && values.length > 0) {
        (values as { label?: string; value?: string }[]).forEach((value, index) => {
          badges.push({
            key: `${key}-${index}`,
            group: `${keyLabel[key]}:`,
            label: `${value?.label || value?.value || value}`,
            onClick: () => removeFilterItem(key, index),
          });
        });
      } else if (typeof values === "string" && values !== "") {
        badges.push({
          key: key,
          group: `${keyLabel[key]}:`,
          label: `${filterBadgeLabel[values] || values}`,
          onClick: () => removeFilter(key),
        });
      }
    });
    return badges;
  }, [filterVisualParams, keyLabel, filterBadgeLabel, removeFilterItem, removeFilter]);

  const breadcrumbItems = [
    { text: t("orders:title"), link: "/sales/orders" }
  ];

  const headers = [
    ...(canChangeStatus ? [{ title: "", key: "checkbox" }] : []),
    { title: t("orders:table.order"), key: "order" },
    { title: t("orders:table.date"), key: "date" },
    { title: t("orders:table.total"), key: "total" },
    { title: t("orders:table.customer"), key: "customer" },
    { title: t("orders:table.payment_method"), key: "payment_method" },
    { title: t("orders:table.payment_status"), key: "payment_status" },
    { title: t("orders:table.processing_status"), key: "processing_status" },
    { title: "", key: "actions" },
  ];

  const toggleSelectOrder = (orderId: string) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleBulkStatusChange = async (status: OrderStatus) => {
    try {
      await Promise.all(
        selectedOrders.map((orderId) => updateOrderStatus(orderId, status))
      );
      toast({
        description: t("orders:status_updated"),
        variant: "success",
      });
    } catch (error: any) {
      toast({
        description: error?.response?.data?.message || t("common:data_submission_error"),
        variant: "destructive",
      });
    }
    setSelectedOrders([]);
  };

  const handleStatusChange = async (orderId: string, status: OrderStatus) => {
    try {
      await updateOrderStatus(orderId, status);
      toast({
        description: t("orders:status_updated"),
        variant: "success",
      });
    } catch (error: any) {
      toast({
        description: error?.response?.data?.message || t("common:data_submission_error"),
        variant: "destructive",
      });
    }
  };

  const openTrackingModal = (order: Order) => {
    setSelectedOrderForTracking(order);
    openModal(TRACKING_MODAL_NAME);
  };

  const openDeliveryFailedModal = (order: Order) => {
    setSelectedOrderForDeliveryFailed(order);
    openModal(DELIVERY_FAILED_MODAL_NAME);
  };

  const openCancelOrderModal = (order: Order) => {
    setSelectedOrderForCancel(order);
    openModal(CANCEL_ORDER_MODAL_NAME);
  };

  return (
    <LayoutInside items={breadcrumbItems}>
      <CustomSheet
        open={modals[FILTER_ORDERS_MODAL]}
        onClose={() => closeModal(FILTER_ORDERS_MODAL)}
      >
        <FilterOrders
          filterValues={{
            products: (filterParams.products as string[] | undefined)?.map((p: string) => {
              const [label, value] = p.includes(':::') ? p.split(':::') : [p, p];
              return { label, value };
            }),
            offers: (filterParams.offers as string[] | undefined)?.map((o: string) => {
              const [label, value] = o.includes(':::') ? o.split(':::') : [o, o];
              return { label, value };
            }),
            affiliates: (filterParams.affiliates as string[] | undefined)?.map((a: string) => {
              const [label, value] = a.includes(':::') ? a.split(':::') : [a, a];
              return { label, value };
            }),
            payment_methods: filterParams.payment_methods as string[],
            status: filterParams.status as string[],
            processing_status: filterParams.processing_status as string[],
          }}
          onFilter={(data) => {
            setActiveTab('all');
            setCurrentPage(1);
            setFilterParams({
              ...data,
              products: data.products?.map(
                (opt) => `${opt.label}:::${opt.value}`
              ),
              offers: data.offers?.map((opt) => `${opt.label}:::${opt.value}`),
              affiliates: data.affiliates?.map(
                (opt) => `${opt.label}:::${opt.value}`
              ),
            } as any);
          }}
        />
      </CustomSheet>

      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        {/* Summary Cards - 2 cards in first row, 3 in second row */}
        <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <CardCurrency
            text={t('orders:delivered')}
            value={String(summaryCounts.delivered)}
            status="paid"
            isLoading={isLoadingSummary}
          />
          <div onClick={() => setActiveTab('open')} className="cursor-pointer">
            <CardCurrency
              text={t('orders:filter_open')}
              value={String(summaryCounts.open)}
              status="period"
              isLoading={isLoadingSummary}
            />
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <CardCurrency
            text={t('orders:returned')}
            value={String(summaryCounts.returned)}
            status="refunded"
            isLoading={isLoadingSummary}
          />
          <div onClick={() => setActiveTab('paid_processing')} className="cursor-pointer">
            <CardCurrency
              text={t('orders:filter_paid_processing')}
              value={String(summaryCounts.paidProcessing)}
              status="refunded"
              isLoading={isLoadingSummary}
            />
          </div>
        </section>

        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            {FILTER_TABS.map((tab) => (
              <Button
                key={tab.key}
                variant={activeTab === tab.key ? "default" : "ghost"}
                size="sm"
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  "gap-2 transition-all",
                  activeTab === tab.key && "shadow-md"
                )}
              >
                <span>{t(tab.labelKey)}</span>
                {activeTab === tab.key && (
                  <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs">
                    {total}
                  </span>
                )}
              </Button>
            ))}
          </div>
        </Card>

        {/* Search and Filter bar */}
        <Card className="p-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t("common:search_placeholder")}
                className="pl-9 w-full"
              />
            </div>
            <div className="flex gap-2 items-center">
              <DateRangePicker
                defaultValue={dateRange}
                onChange={handleDateRangeChange}
              />
              <Button
                variant="secondary"
                onClick={() => openModal(FILTER_ORDERS_MODAL)}
                data-cy="open-filter-orders"
              >
                <ListFilter size={20} />
                <span className="hidden md:inline ml-2">{t("sales:filters")}</span>
              </Button>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mt-4">
              {allFilterBadges.slice(0, MAX_VISIBLE_FILTERS).map((badge) => (
                <Badge
                  key={badge.key}
                  className="flex font-medium min-h-7 items-center hover:opacity-80 hover:bg-[#18181b] gap-4 bg-[#18181b] text-colorText"
                >
                  <div className="flex text-colorText items-center gap-1">
                    <span className="text-muted">{badge.group}</span>
                    {badge.label}
                  </div>
                  <button
                    onClick={badge.onClick}
                    className="text-muted cursor-pointer"
                  >
                    ✕
                  </button>
                </Badge>
              ))}

              {allFilterBadges.length > MAX_VISIBLE_FILTERS && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="cursor-pointer rounded bg-[#27272A] px-1.5 py-0.5 text-xs font-semibold flex items-center justify-center">
                      +{allFilterBadges.length - MAX_VISIBLE_FILTERS}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="w-80 p-4 bg-[#18181b] text-colorText border border-[#333]">
                    <h4 className="font-semibold mb-2">{t("sales:filters")}</h4>
                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto px-4">
                      {allFilterBadges
                        .slice(MAX_VISIBLE_FILTERS)
                        .map((badge) => (
                          <div
                            key={badge.key}
                            className="flex items-center justify-between text-sm"
                          >
                            <div className="gap-1 flex items-start">
                              <span className="text-muted">{badge.group}</span>
                              <span>{badge.label}</span>
                            </div>
                            <button
                              onClick={badge.onClick}
                              className="text-muted hover:text-white ml-2"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}

              <Button
                variant="secondary"
                onClick={clearAllFilters}
                data-cy="clear-filters"
                className="ml-2 h-7 flex items-center gap-4 bg-transparent text-interactive"
              >
                {t("common:clear_all_filters")}
              </Button>
            </div>
          )}
        </Card>

        <AnimatePresence>
          {canChangeStatus && selectedOrders.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Card className="p-3 bg-primary/5 border-primary/20">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium">
                    {selectedOrders.length} {selectedOrders.length === 1 ? 'pedido selecionado' : 'pedidos selecionados'}
                  </span>
                  <Select onValueChange={(value) => handleBulkStatusChange(value as OrderStatus)}>
                    <SelectTrigger className="w-44">
                      <SelectValue placeholder={t("orders:change_status")} />
                    </SelectTrigger>
                    <SelectContent>
                      {getAllowedTransitionsForMultiple(orders, selectedOrders).length === 0 ? (
                        <SelectItem value="_disabled" disabled>
                          {t("orders:no_common_transitions")}
                        </SelectItem>
                      ) : (
                        getAllowedTransitionsForMultiple(orders, selectedOrders).map((status) => (
                          <SelectItem key={status} value={status}>
                            <div className="flex items-center gap-2">
                              {STATUS_ICONS[status]}
                              <span>{t(`orders:status.${status}`)}</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedOrders([])}
                  >
                    Limpar seleção
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Orders Table */}
        <Card className="w-full overflow-x-auto bg-[#18181B]">
          <div className="p-4 flex items-start justify-between gap-4 flex-col md:flex-row">
            <h2 className="text-lg font-medium">{t("orders:title")}</h2>
            <ExportButton
              type="physical-orders"
              filters={exportApiParams}
              label={t("sales:export")}
              title={t("sales:export_modal.title")}
              variant="secondary"
              className="w-full md:w-auto"
              disabled={total === 0}
            />
          </div>
          <TableListCustom
            headers={headers}
            isLoadingData={isLoading}
            data={tableData}
            currentPage={currentPage}
            datacy="orders-table"
            setCurrentPage={setCurrentPage}
            emptyIconMessage={<Search />}
            emptyTitleMessage={t("orders:no_orders")}
            emptyDescriptionMessage={t("orders:no_orders_description")}
          >
            <AnimatePresence mode="wait" initial={false}>
              {orders.map((order, index) => (
                <motion.tr
                  key={order.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2, delay: index * 0.03 }}
                  className={cn(
                    "h-16 text-sm border-t border-zinc-800 transition-colors",
                    selectedOrders.includes(order.id) ? "bg-primary/5" : "hover:bg-secondary/50"
                  )}
                >
                  {canChangeStatus && (
                    <TableCell className="py-4 w-10" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedOrders.includes(order.id)}
                        onCheckedChange={() => toggleSelectOrder(order.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell
                    className={canViewOrderDetails ? "py-4 cursor-pointer" : "py-4"}
                    onClick={canViewOrderDetails ? () => navigate(`/sales/sales-details/${order.id.replace('#', '')}`) : undefined}
                  >
                    <span className="text-muted font-medium">{order.id}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-[#A1A1AA]">{order.created_at_formatted}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-colorText font-medium">{order.total_formatted}</span>
                  </TableCell>
                  <TableCell>
                    <p className="text-colorText">{order.customer.name}</p>
                  </TableCell>
                  <TableCell>
                    {order.payment_method?.type ? (
                      <div className="flex items-center gap-2">
                        {getIconMethodPayment(order.payment_method.type)}
                        <span className="text-muted">{t(`common:${order.payment_method.type}`)}</span>
                      </div>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <BadgeShared status={order.payment_status} />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {order.has_multiple_trackings ? (
                      <div className="flex flex-col gap-1">
                        <BadgeShared
                          status={order.processing_status}
                          text={t("orders:multiple_shipments", {
                            count: order.delivery_trackings?.length ?? 2,
                          })}
                        />
                        <span className="text-xs text-muted">
                          {t("orders:open_details_to_manage_shipments")}
                        </span>
                      </div>
                    ) : canChangeStatus ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-auto p-0 hover:bg-transparent"
                          >
                            <div className="flex items-center gap-1">
                              <BadgeShared
                                status={order.processing_status}
                                text={t(`orders:status.${order.processing_status}`)}
                              />
                              <ChevronDown className="w-3 h-3 text-muted-foreground" />
                            </div>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          className="w-48 bg-zinc-900 border-zinc-700"
                        >
                          {getAllowedTransitionsForDropdown(order.processing_status, order.integration_provider).length === 0 ? (
                            <DropdownMenuItem disabled className="text-muted-foreground">
                              {t("orders:no_transitions_available")}
                            </DropdownMenuItem>
                          ) : (
                            getAllowedTransitionsForDropdown(order.processing_status, order.integration_provider).map((status) => (
                              <DropdownMenuItem
                                key={status}
                                onClick={() => handleStatusChange(order.id, status)}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                {STATUS_ICONS[status]}
                                <span>{t(`orders:status.${status}`)}</span>
                              </DropdownMenuItem>
                            ))
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <BadgeShared
                        status={order.processing_status}
                        text={t(`orders:status.${order.processing_status}`)}
                      />
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <CTATable
                      items={[
                        ...(canAddEditTracking ? [{
                          id: 1,
                          label: 'orders:add_tracking',
                          icon: <Truck className="w-4 h-4" />,
                          action: () => openTrackingModal(order),
                        }] : []),
                        ...(canMarkDeliveryFailure && canMarkAsDeliveryFailed(order.processing_status, order.integration_provider)
                          ? [{
                              id: 3,
                              label: 'orders:mark_delivery_failed',
                              icon: <AlertCircle className="w-4 h-4" />,
                              action: () => openDeliveryFailedModal(order),
                            }]
                          : []),
                        ...((canCancelOrder || canReturnOrder) && canCancelOrReturn(order.processing_status, order.integration_provider)
                          ? [{
                              id: 4,
                              label: 'orders:cancel_or_return',
                              icon: <XCircle className="w-4 h-4" />,
                              action: () => openCancelOrderModal(order),
                            }]
                          : []),
                        ...(canViewOrderDetails ? [{
                          id: 2,
                          label: 'orders:view_details',
                          icon: <Package className="w-4 h-4" />,
                          action: () => navigate(`/sales/sales-details/${order.id.replace('#', '')}`),
                        }] : []),
                      ]}
                    />
                  </TableCell>
                </motion.tr>
              ))}
            </AnimatePresence>
          </TableListCustom>
        </Card>
      </motion.section>

      <TrackingModal order={selectedOrderForTracking} />
      <DeliveryFailedModal order={selectedOrderForDeliveryFailed} />
      <CancelOrderModal order={selectedOrderForCancel} />
    </LayoutInside>
  );
}
